import React, { useEffect, useMemo } from 'react';
import Layout from '@shared/ui/organisms/layout/Layout';
import { HeadlineMedium, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import styled from "styled-components";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center, Row } from '@shared/ui/atoms/StructuralLayout';
import { SortableTable } from '@shared/ui/molecules/SortableTable';
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import Colours from '@shared/ui/atoms/Colours';
import { desktopSmall, desktopWide, tablet } from '@shared/ui/atoms/MediaQueries';
import { selectConfig } from '@shared/store/config.duck';
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment from "moment";
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { Trans, useTranslation } from "react-i18next";
import {
  CarryOverRow,
  loadCarryOverInfo,
  runCarryOver,
  selectData,
  selectHasBeenRun,
  selectLoading
} from "./holiday-carry-over.duck";
import { closeDialog, DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { Tick } from '@shared/ui/atoms/Tick';
import { ProgressBar } from '@shared/ui/atoms/ProgressBar';
import { roundDays } from '@shared/utils/MathUtils';


function TotalAllowanceCell(props: any) {
  const {item} = props
  const {t} = useTranslation();
  const projectedTotal = roundDays(item.thisYearsAllowance + item.thisYearsAdditionalDays + item.daysToCarryOverFromPreviousYear);
  // Compare rounded values - two float sums of the same days can differ in the last bit
  // and would otherwise flag rows where nothing has actually changed.
  const showAlert = projectedTotal !== roundDays(item.totalDays) && item.thisYearsCarryOver > 0;
  if (!props.item.hasLoaded) {
    return <BodySmall weight={400}>...</BodySmall>
  }
  return <div style={{position: 'relative'}}>
    <BodySmall className={'holidayCarryOver__totalCell'} weight={400}>
      {projectedTotal} days {showAlert && <Icon className={'holidayCarryOver__totalCell__icon'} icon={IconTypes.Alert} />}
    </BodySmall>
    {showAlert && <div className={'holidayCarryOver__totalCell__info'}>
      <BodySmall weight={600}>{t('allowance.holiday-carry-over-info.carry-over-days-title-carry', {carriedOverDays: item.thisYearsCarryOver})}</BodySmall>
      <BodySmall>{t('allowance.holiday-carry-over-info.carry-over-days-explanation')}</BodySmall>
    </div>}
  </div>
}

function DaysCarriedOverCell(props: any) {
  const {item} = props
  const {t} = useTranslation();
  const showAlert = roundDays(item.thisYearsCarryOver) !== roundDays(props.value) && item.thisYearsCarryOver > 0;
  if (!props.item.hasLoaded) {
    return <BodySmall weight={400}>...</BodySmall>
  }
  return <div style={{position: 'relative'}}>
    <BodySmall className={'holidayCarryOver__totalCell'} weight={400}>
      {props.value} days {showAlert && <Icon className={'holidayCarryOver__totalCell__icon'} icon={IconTypes.Alert} />}
    </BodySmall>
    {showAlert && <div className={'holidayCarryOver__totalCell__info'}>
      <BodySmall weight={600}>{t('allowance.holiday-carry-over-info.days-to-carry-over-column-alert-title', {carryOverDays: item.thisYearsCarryOver})}</BodySmall>
      <BodySmall>{t('allowance.holiday-carry-over-info.days-to-carry-over-column-alert-explanation')}</BodySmall>
    </div>}
  </div>
}

function ContentCell(props: any) {
  if (!props.item.hasLoaded) {
    return <BodySmall weight={400}>...</BodySmall>
  }
  return <BodySmall weight={400}>{props.value} {props.postfix}</BodySmall>
}

function UpdatedCell(props: any) {
  if (props.value) {
    return <BodySmall weight={400}><Tick /></BodySmall>
  }
  return null;
}

export function NameContentCell(props: any) {
  return <BodySmall weight={400}><a href={window.location.href.includes('/manage/holidays/') ? `/manage/holidays/settings/allowance?userId=${props.value.row.original.userId}` : `/holidays/settings/allowance?userId=${props.value.row.original.userId}`}>{props.value.getValue()}</a></BodySmall>
}

export function HolidayCarryOverPreview(props: Props) {
  document.body.style.backgroundColor = Colours.veryLightGrey;
  const dispatch = useDispatch();
  const hasBeenRun = useSelector(selectHasBeenRun);
  const loading = useSelector(selectLoading);
  const result = useSelector(selectData);
  const config = useSelector(selectConfig);
  const calendarDate = useSelector(selectCalendarDate);
  const {t} = useTranslation();
  const isDataLoading = result.filter((r: CarryOverRow) => !r.loaded).length > 0;

  const holidayStartYear = config.getHolidayYearStartDate(calendarDate) ?? moment();
  const holidayYearEndDate = config.getHolidayYearEndDate(calendarDate) ?? holidayStartYear.add(1, 'year');

  useEffect(() => {
    dispatch(loadCarryOverInfo({calendarYear: calendarDate.year(), skip: 0}))
  }, [calendarDate, dispatch]);

  const holidayUsageColumns = useMemo(() => {
    const year = holidayStartYear.year();
    return [
      {header: 'general.name', accessorKey: 'name', cell: (info: any) => <NameContentCell value={info} /> },

      {header: `allowance.unused-allowance`, translationProps: {year: year - 1}, accessorKey: 'unusedAllowanceFromPreviousYear', cell: (info: any) => <ContentCell item={result.find((r: CarryOverRow) => r.userId === info.row.original.userId)} value={info.getValue()} postfix={'days'} /> },
      {header: `allowance.days-to-carry-over`, translationProps: {year: year}, accessorKey: 'daysToCarryOverFromPreviousYear', cell: (info: any) => <DaysCarriedOverCell value={info.getValue()} item={result.find((r: CarryOverRow) => r.userId === info.row.original.userId)} /> },

      {header: `allowance.basic`, accessorKey: 'thisYearsAllowance', cell: (info: any) => <ContentCell item={result.find((r: CarryOverRow) => r.userId === info.row.original.userId)} value={info.getValue()} postfix={'days'} /> },
      {header: `allowance.just-additional`, accessorKey: 'thisYearsAdditionalDays', cell: (info: any) => <ContentCell item={result.find((r: CarryOverRow) => r.userId === info.row.original.userId)} value={info.getValue()} postfix={'days'} /> },
      {header: `allowance.current-carryover`, accessorKey: 'thisYearsCarryOver', cell: (info: any) => <ContentCell item={result.find((r: CarryOverRow) => r.userId === info.row.original.userId)} value={info.getValue()} postfix={'days'} /> },

      {header: `allowance.projected-total`, translationProps: {year: year}, accessorKey: 'userId', cell: (info: any) => <TotalAllowanceCell item={result.find((r: CarryOverRow) => r.userId === info.getValue())} /> },
      {header: `allowance.updated`, accessorKey: 'updated', cell: (info: any) => <UpdatedCell value={info.getValue()} /> },
    ];
  }, [holidayStartYear, result]);

  const dateRangeString = useMemo(() => {
    const start = holidayStartYear.format('MMMM YYYY');
    const end = holidayYearEndDate.format('MMMM YYYY');
    return `${start} - ${end}`;
  }, [holidayStartYear, holidayYearEndDate]);

  const carryOverExpiryDate = useMemo(() => {
    if (config.autoCarryOverExpiryDay && config.autoCarryOverExpiryMonth) {
      return moment()
        .year(holidayStartYear.year())
        .month(config.autoCarryOverExpiryMonth - 1)
        .date(config.autoCarryOverExpiryDay)
        .format('D MMM YYYY')
    }
    return '';
  }, [config.autoCarryOverExpiryMonth, config.autoCarryOverExpiryDay, holidayStartYear]);

  const openConfirmationPopup = () => {
    dispatch(openDialogWithPayload({
      payload: { previousYear: holidayStartYear.year() - 1, currentYear: holidayStartYear.year(), confirmed: runHolidayCarryOver },
      activeDialog: DialogIdentifiers.HolidayRollOverConfirmationDialog
    }));
  }

  const runHolidayCarryOver = async () => {
    dispatch(runCarryOver({calendarYear: holidayStartYear.year()}))
    dispatch(closeDialog());
  }

  const completed = useMemo(() => {
    return (result.filter(r => r.loaded).length / result.length) * 100;
  }, [result])

  return (
    <>
      <Layout fullWidth={true} hideFooter={true}>

        {loading ? <Center><LoadingSpinner style={{marginTop: 80}} hideBorder={true} message={`This can take several minutes`} /></Center> : <>

          <RibbonContainer>
            <RibbonContentContainer>
              <BodyRegular weight={600}>Showing all users</BodyRegular>
              <div>
                <OutlineButton link={'/manage/holidays'} icon={IconTypes.Settings} text={'settings-page.holiday'} />
              </div>
            </RibbonContentContainer>
          </RibbonContainer>

          <ContentContainer>
            <HeaderRow>
              <HeadlineMedium color={Colours.veryDarkBlue}>{t('allowance.holiday-carry-over-info.carry-over-report-title')}</HeadlineMedium>
              <DateHeader>
                <HeadlineSmall>{dateRangeString}</HeadlineSmall>
                <DateFlicker className={'userSectionHeader__date'}
                             date={holidayStartYear}
                             hideDateText={true}
                             dateScale={DateScale.Year}
                             disabled={loading || isDataLoading}
                             updateGlobalDate={true} />
              </DateHeader>
            </HeaderRow>

            <InformationRow>
              <FlatContentCard className={'holidayCarryOver__info'}>
                <Row>
                  <Icon icon={IconTypes.Alert} />
                  <BodySmall weight={400}><Trans i18nKey="allowance.holiday-carry-over-info.info" /></BodySmall>
                </Row>
              </FlatContentCard>
              <div className={'holidayCarryOver__infoContainer'}>
                <div className={'holidayCarryOver__infoLink'}>
                  <BodySmall weight={600}>{t('allowance.holiday-carry-over-info.carry-over-left', {carryOverDays: config.autoCarryOverDaysLimit})}</BodySmall>
                  <BodySmall weight={400}><Trans i18nKey={'allowance.holiday-carry-over-info.leave-settings'} components={[<a href="/manage/holidays">Leave settings</a>]} /></BodySmall>
                </div>
                <div className={'holidayCarryOver__infoLink'}>
                  <BodySmall weight={600} colour={Colours.alertRed}>{!carryOverExpiryDate ? t('allowance.holiday-carry-over-info.carry-over-does-not-expire') : t('allowance.holiday-carry-over-info.carry-over-expires', {carryOverExpiryDate: carryOverExpiryDate})}</BodySmall>
                  <BodySmall weight={400}>
                    <a href={window.location.href.includes('/manage/holidays/') ? "/manage/holidays/settings/allowance-carry-over-expiry-days" : "/holidays/settings/allowance-carry-over-expiry-days"}>{t('allowance.holiday-carry-over-info.view-report')}</a>
                  </BodySmall>
                </div>
              </div>
            </InformationRow>
            {completed < 99 && <ProgressBar complete={completed} style={{marginBottom: 32}} />}
            {hasBeenRun && <FlatContentCard style={{marginBottom: 32}}>
              <BodyLarge colour={Colours.alertRed}>
                <Trans i18nKey="allowance.holiday-carry-over-has-been-run" />
              </BodyLarge>
            </FlatContentCard>}
              <>
                {holidayStartYear && <AdditionalHeaderRow>
                  <BodySmall style={{marginLeft: '60%'}} weight={600} colour={Colours.darkGrey}>{holidayStartYear.year()} Allowance</BodySmall>
                </AdditionalHeaderRow>}
                <SortableTable data={result}
                               columns={holidayUsageColumns} />

                <PrimaryButton style={{marginLeft: 'auto', marginTop: 24}}
                               text={'allowance.rollover'} disabled={loading || hasBeenRun || isDataLoading}
                               click={() => openConfirmationPopup()} />
              </>
          </ContentContainer>
        </>}
      </Layout>
    </>
  )
}

interface Props {
}

const AdditionalHeaderRow = styled.div`
  width: 100%;
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: white;
  padding: 48px 64px;
  margin: 24px auto 0 auto;

  @media (${tablet}) {
     padding: 48px 64px;
     border-radius: 24px;
   }

  .holidayCarryOver__totalCell {
    display: flex;
    i {
      margin-left: 4px;
    }
    i:hover {
      display: block;
    }
  }

  .holidayCarryOver__totalCell:hover~.holidayCarryOver__totalCell__info {
    display: block;
  }

  .holidayCarryOver__totalCell__info {
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
    z-index: 1000;
    padding: 10px;
    left: 40px;
    position: absolute;
    display: none;
    min-width: 280px;
    max-width: 280px;
  }

  th p {
    text-align: left;
  }
  table thead {
    border-bottom-color: ${Colours.mildGrey};
  }
  table th:nth-child(3) i {
    border-right: solid 1px ${Colours.mildGrey};
    padding-right: 8px;
    margin-right: -8px;
  }
  table th:nth-child(6) i {
    border-right: solid 1px ${Colours.mildGrey};
    padding-right: 8px;
    margin-right: -8px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .weekFlicker {
    width: 100px;
  }
  .linkButton {
    margin: 0;
    width: unset;
    max-width: unset;
  }
`

const InformationRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;
  .holidayCarryOver__info {
    max-width: 550px;
    padding: 24px 32px;
    i {
      margin-right: 10px;
      margin-top: 4px;
    }
  }
  .holidayCarryOver__infoContainer {
    display: flex;
    width: 100%;
    align-items: center;
    margin-top: 16px;
  }

  @media (${tablet}) {
    .holidayCarryOver__infoLink {
      margin-left: 16px;
    }
  }
  @media (${desktopSmall}) {
    flex-direction: row;
    .holidayCarryOver__infoContainer {
      margin-top: 0;
    }
  }
  @media (${desktopWide}) {
    .holidayCarryOver__infoLink {
      margin-left: auto;
    }
  }
`

const DateHeader = styled.div`
  display: flex;
  align-items: center;
`

const RibbonContainer = styled.div`
  background-color: white;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`
